import { useState } from 'react'
import { addBusinessAppPerson, type CustomerPerson, type NewPerson } from '../api/businessApps'
import './OwnerPicker.css'

/**
 * PROPRIETARUL APLICATIEI LA CLIENT - alegere din oamenii clientului, sau
 * "+ Add a person" scris pe loc.
 *
 * Coordonatoarea, 6 octombrie 2026, la pasul "Service owner at the customer"
 * din "Complete": "aici ar trebui sa poata introduce, tu ai pus din lista
 * noastra.. nu sunt externi?". Lista oamenilor firmei e plina de utilizatorii
 * de pe calculatoare, iar omul care raspunde de aplicatie de multe ori nu e in
 * ea. Deci:
 *
 *  - lista are cautare (nume, functie, email) si arata functia si adresa
 *    langa nume; persoanele de contact ale clientului (`isContact`) stau
 *    primele, in grupul lor;
 *  - "+ Add a person" deschide pe loc Name / Role / Email / Phone. Persoana
 *    NU se scrie aici: o scrie cine salveaza (`saveOwnerDraft`), ca persoana de
 *    contact a clientului, si abia apoi aplicatia primeste `owner_contact_id`.
 *
 * Aceeasi bucata sta in fereastra "Complete" (`QuickComplete`) si in
 * formularul aplicatiei (`AppFormModal`, adaugare din lista si Edit din fisa).
 * Textul scris de om ajunge in pagina ca text React - scapat.
 */

export const blankPerson = (name = ''): NewPerson => ({ name, role: '', email: '', phone: '' })

/** Ce se vede in lista: `Nume - functie, email`. */
export function personLabel(p: CustomerPerson): string {
  const extra = [p.role ?? '', p.email ?? ''].map((x) => x.trim()).filter((x) => x !== '')
  return extra.length > 0 ? `${p.name} - ${extra.join(', ')}` : p.name
}

const matches = (p: CustomerPerson, search: string) => {
  const q = search.trim().toLowerCase()
  if (q === '') return true
  return [p.name, p.role ?? '', p.email ?? ''].some((x) => x.toLowerCase().includes(q))
}

/** Mesajul care opreste salvarea unei persoane noi; null = se poate scrie. */
export function draftProblem(draft: NewPerson | null): string | null {
  if (draft === null) return null
  if (draft.name.trim() === '') return 'Write the name of the new person, or pick someone from the list.'
  const email = draft.email.trim()
  if (email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'The email of the new person is not a valid address.'
  return null
}

/**
 * Scrie persoana noua la client si intoarce ID-ul ei. Arunca cu mesajul
 * serverului (`ApiError`) daca nu s-a putut - cine cheama il arata.
 */
export async function saveOwnerDraft(companyId: number, draft: NewPerson): Promise<CustomerPerson> {
  const result = await addBusinessAppPerson(companyId, {
    name: draft.name.trim(),
    role: draft.role.trim(),
    email: draft.email.trim(),
    phone: draft.phone.trim(),
  })
  if (result.status !== 'OK' || !result.id) throw new Error(result.message || 'The person could not be added.')
  return result.person ?? { id: result.id, name: draft.name.trim(), role: draft.role.trim(), email: draft.email.trim(), isContact: true }
}

export function OwnerPicker({
  selectId,
  people,
  value,
  onChange,
  draft,
  onDraft,
  emptyLabel,
  customerName,
  disabled = false,
}: {
  /** `id` pe selector (`qcOwnerSelect`, `appOwner`); restul campurilor il au ca prefix. */
  selectId: string
  people: CustomerPerson[]
  value: number
  onChange(id: number): void
  /** Persoana scrisa pe loc; null = se alege din lista. */
  draft: NewPerson | null
  onDraft(draft: NewPerson | null): void
  emptyLabel: string
  customerName?: string
  disabled?: boolean
}) {
  const [search, setSearch] = useState('')

  if (draft !== null) {
    const set = (patch: Partial<NewPerson>) => onDraft({ ...draft, ...patch })
    const unreachable = draft.email.trim() === '' && draft.phone.trim() === ''
    return (
      <div className="app-owner-new" id={`${selectId}New`}>
        <div className="row">
          <div className="col-sm-6 form-group">
            <label htmlFor={`${selectId}NewName`}>
              Name <span className="app-owner-req">*</span>
            </label>
            <input
              id={`${selectId}NewName`}
              className="form-control"
              maxLength={100}
              placeholder="e.g. Andreea Marin"
              value={draft.name}
              disabled={disabled}
              autoFocus
              onChange={(e) => set({ name: e.target.value })}
            />
          </div>
          <div className="col-sm-6 form-group">
            <label htmlFor={`${selectId}NewRole`}>Role / title</label>
            <input
              id={`${selectId}NewRole`}
              className="form-control"
              maxLength={100}
              placeholder="e.g. IT manager"
              value={draft.role}
              disabled={disabled}
              onChange={(e) => set({ role: e.target.value })}
            />
          </div>
          <div className="col-sm-6 form-group">
            <label htmlFor={`${selectId}NewEmail`}>Email</label>
            <input
              id={`${selectId}NewEmail`}
              type="email"
              className="form-control"
              maxLength={100}
              placeholder="name@customer.com"
              value={draft.email}
              disabled={disabled}
              onChange={(e) => set({ email: e.target.value })}
            />
          </div>
          <div className="col-sm-6 form-group">
            <label htmlFor={`${selectId}NewPhone`}>Phone</label>
            <input
              id={`${selectId}NewPhone`}
              className="form-control"
              maxLength={100}
              placeholder="+40 7xx xxx xxx"
              value={draft.phone}
              disabled={disabled}
              onChange={(e) => set({ phone: e.target.value })}
            />
          </div>
        </div>
        <p className="app-owner-note">
          {unreachable ? <strong>Add an email or a phone so we can reach them. </strong> : null}
          They are saved as a contact person of {customerName && customerName !== '' ? customerName : 'the customer'} when you press Save.{' '}
          <button type="button" className="btn btn-link btn-xs app-owner-switch" id={`${selectId}Pick`} onClick={() => onDraft(null)}>
            Pick from the list instead
          </button>
        </p>
      </div>
    )
  }

  /* Cel ales ramane in lista oricat s-ar ingusta cautarea - altfel selectorul
     ar arata alt om decat cel care se salveaza. */
  const shown = people.filter((p) => p.id === value || matches(p, search))
  const contacts = shown.filter((p) => p.isContact === true)
  const others = shown.filter((p) => p.isContact !== true)
  const hits = people.filter((p) => matches(p, search)).length
  const option = (p: CustomerPerson) => (
    <option key={p.id} value={p.id}>
      {personLabel(p)}
    </option>
  )

  return (
    <div className="app-owner-pick">
      {people.length > 0 ? (
        <input
          id={`${selectId}Search`}
          type="search"
          className="form-control input-sm app-owner-search"
          aria-label="Search the customer's people"
          placeholder="Search by name, role or email"
          value={search}
          disabled={disabled}
          onChange={(e) => setSearch(e.target.value)}
        />
      ) : null}
      <div className="app-owner-row">
        <select id={selectId} className="form-control" aria-label="Service owner" value={value} disabled={disabled} onChange={(e) => onChange(Number(e.target.value))}>
          <option value={0}>{emptyLabel}</option>
          {contacts.length > 0 && others.length > 0 ? (
            <>
              <optgroup label="Customer contacts">{contacts.map(option)}</optgroup>
              <optgroup label="Other people at the customer">{others.map(option)}</optgroup>
            </>
          ) : (
            shown.map(option)
          )}
        </select>
        <button
          type="button"
          className="btn btn-default"
          id={`${selectId}Add`}
          disabled={disabled}
          onClick={() => onDraft(blankPerson(hits === 0 ? search.trim() : ''))}
        >
          <i className="fas fa-plus" aria-hidden="true"></i> Add a person
        </button>
      </div>
      {people.length === 0 ? (
        <p className="app-owner-note">No one is recorded for this customer yet - add the person with &quot;Add a person&quot;.</p>
      ) : search.trim() !== '' && hits === 0 ? (
        <p className="app-owner-note">
          Nobody at the customer matches &quot;{search.trim()}&quot;. If they are not there, add them with &quot;Add a person&quot;.
        </p>
      ) : null}
    </div>
  )
}
